import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Flex, Heading, IconButton, Text } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { ArrowLeft, FileText } from 'phosphor-react';

import { GlassPageBg } from '../../../../../components/glass';
import { useVoltar } from '../useVoltar';
import { AbaComoDecide } from './AbaComoDecide';
import { AbaEntender } from './AbaEntender';
import { ArvorePlanoEmpresa } from './ArvorePlanoEmpresa';
import { FadeIn } from './ui';

type Aba = 'entender' | 'plano' | 'decide';

const ABAS: { id: Aba; label: string }[] = [
  { id: 'entender', label: 'Entender' },
  { id: 'plano', label: 'Plano da empresa' },
  { id: 'decide', label: 'Como o ContaCil decide' },
];

export function ConciliacaoContabilPlanoContasPage() {
  const voltar = useVoltar();
  // Atalho `?empresa=<id>` da pagina Empresas pula direto pra aba 'plano'
  // — o usuario clicou "Plano de contas" de UMA empresa, espera ver o plano
  // dela, nao o tutorial de "Entender".
  const [params] = useSearchParams();
  const temEmpresaParam = !!params.get('empresa');
  const [aba, setAba] = useState<Aba>(temEmpresaParam ? 'plano' : 'entender');

  return (
    <GlassPageBg>
      <Box maxW="1280px" mx="auto" w="full" px={{ base: 4, md: 8 }} py={{ base: 6, md: 10 }}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: [0.4, 0, 0.2, 1] }}
        >
          <Flex align="center" gap={3} mb={5}>
            <IconButton
              aria-label="Voltar"
              icon={<ArrowLeft size={18} weight="bold" />}
              variant="ghost"
              size="sm"
              borderRadius="full"
              color="#64748B"
              _hover={{ bg: 'rgba(15, 23, 42, 0.06)', color: '#0F172A' }}
              onClick={voltar}
            />
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              w="64px"
              h="64px"
              borderRadius="2xl"
              bgGradient="linear(135deg, #E0E7FF 0%, #DDD6FE 100%)"
              color="#6366F1"
              boxShadow="0 8px 24px -6px rgba(99, 102, 241, 0.4)"
              flexShrink={0}
            >
              <FileText size={32} weight="duotone" />
            </Box>
            <Box>
              <Text
                fontSize="11px"
                color="#94A3B8"
                textTransform="uppercase"
                letterSpacing="0.1em"
                fontWeight="semibold"
              >
                Conciliação Contábil
              </Text>
              <Heading fontSize={{ base: '22px', md: '28px' }} fontWeight="700" color="#0F172A">
                Plano de Contas
              </Heading>
              <Text fontSize="sm" color="#64748B" mt={0.5}>
                O mapa de para onde cada real vai na contabilidade — e como o ContaCil decide isso.
              </Text>
            </Box>
          </Flex>
        </motion.div>

        {/* Abas (pílulas glass) */}
        <Flex
          role="tablist"
          aria-label="Seções do Plano de Contas"
          display="inline-flex"
          gap="2px"
          p="4px"
          mb={6}
          borderRadius="full"
          bg="rgba(255, 255, 255, 0.72)"
          backdropFilter="blur(20px) saturate(180%)"
          sx={{ WebkitBackdropFilter: 'blur(20px) saturate(180%)' }}
          borderWidth="1px"
          borderColor="rgba(255, 255, 255, 0.6)"
          maxW="full"
          overflowX="auto"
        >
          {ABAS.map(a => {
            const on = aba === a.id;
            return (
              <Box
                key={a.id}
                as="button"
                role="tab"
                aria-selected={on}
                onClick={() => setAba(a.id)}
                px={4}
                py={2}
                borderRadius="full"
                fontSize="13px"
                fontWeight="600"
                whiteSpace="nowrap"
                flexShrink={0}
                color={on ? '#6366F1' : '#64748B'}
                bg={on ? 'white' : 'transparent'}
                boxShadow={on ? '0 2px 8px -2px rgba(15, 23, 42, 0.12)' : 'none'}
                transition="all 0.2s"
                _hover={on ? {} : { color: '#0F172A' }}
              >
                {a.label}
              </Box>
            );
          })}
        </Flex>

        {/* Conteúdo da aba */}
        <FadeIn key={aba}>
          {aba === 'entender' && <AbaEntender />}
          {aba === 'plano' && <ArvorePlanoEmpresa />}
          {aba === 'decide' && <AbaComoDecide />}
        </FadeIn>
      </Box>
    </GlassPageBg>
  );
}
